/* tslint:disable:max-line-length */
import React, { useMemo } from 'react';
import { Handle, Position, NodeProps } from 'react-flow-renderer';

import { ClockCircleOutlined } from '@ant-design/icons';
import { sum } from 'lodash';

import { useViewActions } from '@mm/state/actions/viewActions';
import { useSelector } from 'react-redux';
import { StoreState } from '@mm/state/store-interfaces';
import {
  CampaignTypeWithMappedPurposeAndDisplayStatus,
  selectCampaignById,
} from '@mm/state/selectors/campaignSelectors';
import { useCampaignCRUDActions } from '@mm/state/actions/CampaignsCRUDActions';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import moment from 'moment';
import { Date } from '../elements/Date/Date';
import { ITime } from '../../../../../../../../components/SlideoutMenu/menus/SlideoutDateTime/SlideoutDateTime';
import { Measure } from '../../../../../../../../../common/widgets/DelayWidget/TimePeriodSelector/TimePeriodSelector';
import { pickGranularity } from '../../../../../../../campaigns/components/DripCampaignTimelineItem/TimelineHeader/TimelineHeader';
import { CampaignType } from '@mm/state/model/CampaignsModel';

import css from './Scheduler.module.css';
import classNames from 'classnames';

type Data = {
  campaignId: number;
  intervalInMin?: number;
  schedulerDateTimeVisible?: boolean;
};

type Props = { className?: string; withHandle?: boolean; data: Data } & Partial<
  NodeProps<Data>
>;

export const DelayIcon = ClockCircleOutlined;

export const Scheduler = ({
  className,
  data,
  isDragging = false,
  withHandle = true,
}: Props) => {
  const chatbotId = useSelector(activeChatbotIdSelector);

  const { campaignId, schedulerDateTimeVisible } = data || {};

  const campaign = useSelector<
    StoreState,
    CampaignTypeWithMappedPurposeAndDisplayStatus | undefined
  >(state => selectCampaignById(state, data.campaignId));

  const [scheduler] = campaign?.schedulersAttributes || [];
  const intervalInMins = scheduler?.intervalInMin || data.intervalInMin || 0;
  const [counter = 0, granularity] = pickGranularity(intervalInMins);

  const date = scheduler?.performAtTime;

  const [_, ...rest] = Array.from(granularity).reverse();
  const measure = rest.reverse().join('').toLowerCase() as Measure;

  const viewActions = useViewActions();

  const campaignCRUDActions = useCampaignCRUDActions();

  const time: ITime = useMemo(() => {
    return {
      date,
      value: counter,
      measure,
    };
  }, [date, counter, measure]);

  const onSubmit = async (time: ITime, closeModal) => {
    console.debug(`Scheduler: onSubmit: `, time);

    if (scheduler) {
      try {
        await campaignCRUDActions.updateCampaign(
          {
            id: campaignId,
            schedulersAttributes: [
              {
                ...scheduler,
                performAtTime: time.date,
                intervalInMin: time.measure
                  ? moment.duration(time.value, time.measure).asMinutes()
                  : null,
              },
            ],
          } as CampaignType,
          chatbotId,
        );
        closeModal();
      } catch (err) {}
    }
  };

  const placeholder = date ? 'Wait until' : 'Wait for';

  const onDateClick = () => {
    console.debug(`Scheduler: onDateClick: `, { data });

    if (isDragging) {
      return;
    }

    viewActions.showSlideoutMenu('dateTime', {
      title: 'Wait',
      onSubmit,
      time: {
        date: scheduler.performAtTime,
        value: counter,
        measure,
      },
    });
  };

  const awaitingCount = sum([
    scheduler?.currentQueuedCount,
    scheduler?.instagramCurrentQueuedCount,
    scheduler?.smsCurrentQueuedCount,
    scheduler?.webchatCurrentQueuedCount,
  ]);

  return (
    <div className={classNames(className, css.container)}>
      {withHandle && <Handle type="target" position={Position.Top} />}

      <span
        className={classNames(css.root, {
          [css.withLine]: !!schedulerDateTimeVisible,
        })}
      >
        <svg
          width="36"
          height="36"
          viewBox="0 0 36 36"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M26.2 14.58a.96.96 0 0 0-.8-.44h-6.76V3.54a.96.96 0 0 0-1.84-.4L9.08 20.5a.96.96 0 0 0 .88 1.36h6.75v10.6a.96.96 0 0 0 1.85.4l7.71-17.36c.14-.3.11-.64-.07-.92Z"
            fill="#FAAD14"
          />
        </svg>
      </span>

      {!!schedulerDateTimeVisible && (
        <Date
          className={css.dateTimeNode}
          placeholder={placeholder}
          time={time}
          awaitingCount={awaitingCount}
          onClick={onDateClick}
        />
      )}

      {withHandle && <Handle type="source" position={Position.Bottom} />}
    </div>
  );
};
